import type { CrudFieldConfig } from "components/admin/workspace-crud";
import {
  buildMinMaxValidator,
  buildOptionalHexColorValidator,
  buildOptionalPhoneValidator,
  buildSlugValidator,
} from "components/admin/forms/validation";
import { formatPortalLabel, hasAnyPortalAction, hasPortalAction } from "utils/portal-access";

import {
  schoolDocumentManageActions,
  schoolDocumentVerifyActions,
  schoolGalleryManageActions,
  toSelectOptions,
} from "pages/app/workspace/workspace-helpers";

export const buildAdministrativeRegionFields = ({
  countries,
  regions,
}: {
  countries: any[];
  regions: any[];
}): CrudFieldConfig[] => [
  {
    name: "country_id",
    label: "Country",
    kind: "select",
    options: toSelectOptions(countries, (country) => country.name || "Unknown"),
    required: true,
    clearFieldsOnChange: ["parent_id"],
  },
  {
    name: "parent_id",
    label: "Parent region",
    kind: "select",
    options: (values) =>
      toSelectOptions(
        regions.filter(
          (region: any) =>
            !values.country_id || String(region.country_id) === String(values.country_id),
        ),
        (region) => region.name || "Unknown",
      ),
    placeholder: "No parent region",
  },
  {
    name: "name",
    label: "Region name",
    required: true,
  },
  {
    name: "code",
    label: "Code",
    placeholder: "GP, WC, NBO",
  },
  {
    name: "kind",
    label: "Kind",
    kind: "select",
    options: [
      { label: "Province", value: "province" },
      { label: "County", value: "county" },
      { label: "State", value: "state" },
      { label: "District", value: "district" },
      { label: "City", value: "city" },
    ],
    required: true,
  },
];

export const buildAdministrativeRegionCreateValues = (countryId: string, countries: any[]) => ({
  country_id: countryId || countries?.[0]?.id || "",
  parent_id: "",
  name: "",
  code: "",
  kind: "province",
});

export const buildAdministrativeRegionUpdateValues = (row: any) => ({
  country_id: row.country_id || "",
  parent_id: row.parent_id || "",
  name: row.name || "",
  code: row.code || "",
  kind: row.kind || "province",
});

export const buildSportAgeGroupFields = (sports: any[]): CrudFieldConfig[] => [
  {
    name: "sport_id",
    label: "Sport",
    kind: "select",
    options: toSelectOptions(sports, (sport) => sport.name || "Unknown"),
    helper: "Anchor the age group to the sport it will appear under in team and roster flows.",
    required: true,
  },
  {
    name: "name",
    label: "Age group name",
    placeholder: "Under 19",
    helper: "Use the public-facing label coaches and admins will recognise quickly.",
    required: true,
  },
  {
    name: "gender_category",
    label: "Gender category",
    kind: "select",
    options: [
      { label: "Mixed", value: "mixed" },
      { label: "Boys", value: "boys" },
      { label: "Girls", value: "girls" },
    ],
    helper: "Helps separate parallel boys, girls, and mixed structures within the same sport.",
    required: true,
  },
  {
    name: "min_age",
    label: "Minimum age",
    kind: "number",
    min: 0,
    placeholder: "13",
    helper: "Leave blank if the group only has an upper limit.",
  },
  {
    name: "max_age",
    label: "Maximum age",
    kind: "number",
    min: 0,
    placeholder: "18",
    helper: "Use the oldest eligible age for this group.",
    validate: buildMinMaxValidator({
      label: "Maximum age",
      otherField: "min_age",
      relation: "gte",
    }),
  },
  {
    name: "sort_order",
    label: "Sort order",
    kind: "number",
    min: 0,
    placeholder: "10",
    helper: "Lower numbers appear earlier in ordered lists.",
  },
  {
    name: "team_label",
    label: "Team label",
    placeholder: "1st XI, A team, Colts",
    helper: "Optional short-hand used by school teams built from this age band.",
  },
];

export const buildSportAgeGroupCreateValues = (sportId: string, sports: any[]) => ({
  sport_id: sportId || sports?.[0]?.id || "",
  name: "",
  min_age: "",
  max_age: "",
  sort_order: "",
  team_label: "",
  gender_category: "mixed",
});

export const buildSportAgeGroupUpdateValues = (row: any) => ({
  sport_id: row.sport_id || "",
  name: row.name || "",
  min_age: row.min_age ?? "",
  max_age: row.max_age ?? "",
  sort_order: row.sort_order ?? "",
  team_label: row.team_label || "",
  gender_category: row.gender_category || "mixed",
});

export const buildSchoolFields = ({
  countries,
  regions,
}: {
  countries: any[];
  regions: any[];
}): CrudFieldConfig[] => [
  {
    name: "name",
    label: "School name",
    required: true,
  },
  {
    name: "slug",
    label: "Slug",
    placeholder: "st-stithians-college",
    required: true,
    validate: buildSlugValidator(),
  },
  {
    name: "status",
    label: "Status",
    kind: "select",
    options: [
      { label: "Prospect", value: "prospect" },
      { label: "Onboarding", value: "onboarding" },
      { label: "Active", value: "active" },
      { label: "Inactive", value: "inactive" },
    ],
    required: true,
  },
  {
    name: "school_type",
    label: "School type",
    placeholder: "independent, public, academy",
  },
  {
    name: "website",
    label: "Website",
    placeholder: "https://school.example.com",
  },
  {
    name: "phone_number",
    label: "Phone number",
    placeholder: "0115776000",
    validate: buildOptionalPhoneValidator("Phone number"),
  },
  {
    name: "email",
    label: "Email",
    kind: "email",
    placeholder: "sport@school.org",
  },
  {
    name: "primary_color",
    label: "Primary color",
    placeholder: "#1D4ED8",
    helper: "Hex value used by the updated school branding payload.",
    validate: buildOptionalHexColorValidator("Primary color"),
  },
  {
    name: "secondary_color",
    label: "Secondary color",
    placeholder: "#0F172A",
    validate: buildOptionalHexColorValidator("Secondary color"),
  },
  {
    name: "accent_color",
    label: "Accent color",
    placeholder: "#F59E0B",
    validate: buildOptionalHexColorValidator("Accent color"),
  },
  {
    name: "theme_mode",
    label: "Theme mode",
    kind: "select",
    options: [
      { label: "Light", value: "light" },
      { label: "Dark", value: "dark" },
      { label: "Accessible", value: "accessible" },
    ],
    required: true,
  },
  {
    name: "image",
    label: "School image",
    kind: "image",
    helper: "Upload a logo or paste an image URL for the school profile.",
  },
  {
    name: "locations",
    label: "Locations",
    kind: "object_array",
    addLabel: "Add location",
    validate: (value) => {
      const locations = Array.isArray(value) ? value : [];
      const primaryLocations = locations.filter((location: any) => location?.primary_location);

      if (!locations.length) {
        return "Add at least one location.";
      }

      if (!primaryLocations.length) {
        return "Mark one location as the primary location.";
      }

      if (primaryLocations.length > 1) {
        return "Only one location can be marked as the primary location.";
      }

      return undefined;
    },
    createItem: () => ({
      country_id: "",
      administrative_region_id: "",
      locality: "",
      address_line1: "",
      address_line2: "",
      postal_code: "",
      primary_location: false,
    }),
    itemFields: [
      {
        name: "country_id",
        label: "Country",
        kind: "select",
        options: toSelectOptions(countries, (country) => country.name || "Unknown"),
        required: true,
      },
      {
        name: "administrative_region_id",
        label: "Administrative region",
        kind: "select",
        options: (_values, context) =>
          toSelectOptions(
            regions.filter(
              (region: any) =>
                !context?.item?.country_id ||
                String(region.country_id) === String(context.item.country_id),
            ),
            (region) =>
              `${region.name || "Unknown"}${
                region.kind ? ` | ${formatPortalLabel(region.kind)}` : ""
              }`,
          ),
        placeholder: "No region",
      },
      {
        name: "locality",
        label: "Locality",
        placeholder: "Johannesburg, Cape Town",
      },
      {
        name: "postal_code",
        label: "Postal code",
        placeholder: "2191",
      },
      {
        name: "address_line1",
        label: "Address line 1",
        placeholder: "40 Peter Place",
      },
      {
        name: "address_line2",
        label: "Address line 2",
        placeholder: "Building, campus, suburb",
      },
      {
        name: "primary_location",
        label: "Primary location",
        kind: "checkbox",
      },
    ],
  },
  {
    name: "metadata",
    label: "Metadata",
    kind: "key_value",
    helper: "Optional flexible fields like onboarding_stage or external IDs.",
  },
];

export const buildSchoolCreateValues = () => ({
  name: "",
  slug: "",
  status: "prospect",
  school_type: "",
  website: "",
  phone_number: "",
  email: "",
  primary_color: "#1D4ED8",
  secondary_color: "#0F172A",
  accent_color: "#F59E0B",
  theme_mode: "light",
  image: "",
  metadata: {},
  locations: [
    {
      country_id: "",
      administrative_region_id: "",
      locality: "",
      address_line1: "",
      address_line2: "",
      postal_code: "",
      primary_location: true,
    },
  ],
});

export const buildSchoolUpdateValues = (row: any) => ({
  name: row.name || "",
  slug: row.slug || "",
  status: row.status || "prospect",
  school_type: row.school_type || "",
  website: row.website || "",
  phone_number: row.phone_number || "",
  email: row.email || "",
  primary_color: row.branding?.primary_color || "",
  secondary_color: row.branding?.secondary_color || "",
  accent_color: row.branding?.accent_color || "",
  theme_mode: row.branding?.theme_mode || "light",
  image: row.image_url || row.image || "",
  metadata: row.metadata || {},
  locations:
    (row.school_locations || []).length > 0
      ? (row.school_locations || []).map((location: any) => ({
          country_id: location.country_id || location.country?.id || "",
          administrative_region_id:
            location.administrative_region_id || location.administrative_region?.id || "",
          locality: location.locality || "",
          address_line1: location.address_line1 || "",
          address_line2: location.address_line2 || "",
          postal_code: location.postal_code || "",
          primary_location: Boolean(location.primary_location),
        }))
      : [
          {
            country_id: "",
            administrative_region_id: "",
            locality: "",
            address_line1: "",
            address_line2: "",
            postal_code: "",
            primary_location: true,
          },
        ],
});

export const buildSchoolRoleOptions = (portalAccess: any) => [
  ...(hasAnyPortalAction(portalAccess, [
    "school_role_assignments.manage",
    "school_role_assignments.assign_head_of_sports",
  ])
    ? [{ label: "Head of Sports", value: "head_of_sports" }]
    : []),
  ...(hasAnyPortalAction(portalAccess, [
    "school_role_assignments.manage",
    "school_role_assignments.assign_head_of_sport",
  ])
    ? [{ label: "Head of Sport", value: "head_of_sport" }]
    : []),
  ...(hasAnyPortalAction(portalAccess, [
    "school_role_assignments.manage",
    "school_role_assignments.assign_coach",
  ])
    ? [{ label: "Coach", value: "coach" }]
    : []),
  ...(hasPortalAction(portalAccess, "school_role_assignments.manage")
    ? [{ label: "Brand Ambassador", value: "brand_ambassador" }]
    : []),
];

export const buildSchoolRoleFields = ({
  assignments = [],
  roleOptions,
  schoolSports,
  users,
}: {
  assignments?: any[];
  roleOptions: { label: string; value: string }[];
  schoolSports: any[];
  users: any[];
}): CrudFieldConfig[] => [
  {
    name: "role",
    label: "Role",
    kind: "select",
    options: roleOptions,
    required: true,
    clearFieldsOnChange: (nextValue) =>
      ["head_of_sports", "brand_ambassador"].includes(String(nextValue)) ? ["sport_id"] : [],
  },
  {
    name: "user_id",
    label: "User",
    kind: "select",
    options: (values) => {
      const options = toSelectOptions(users, (user) => {
        const activeAssignments = Array.isArray(user.current_assignments)
          ? user.current_assignments.length
          : 0;
        const assignmentLabel = activeAssignments
          ? ` | ${activeAssignments} active role${activeAssignments === 1 ? "" : "s"}`
          : "";
        return `${user.full_name || user.name || user.email}${
          user.email ? ` | ${user.email}` : ""
        }${assignmentLabel}`;
      });
      const selectedUserId = String(values.user_id || "");

      // Editing an assignment must still show its user even when candidates exclude them.
      if (selectedUserId && !options.some((option) => String(option.value) === selectedUserId)) {
        const assignment = assignments.find(
          (current: any) => String(current.user_id) === selectedUserId,
        );

        return [
          { label: assignment?.user_name || "Current assignee", value: selectedUserId },
          ...options,
        ];
      }

      return options;
    },
    required: true,
  },
  {
    name: "sport_id",
    label: "Sport",
    kind: "select",
    options: toSelectOptions(
      schoolSports,
      (schoolSport) => schoolSport.sport_name || "Unknown",
      (schoolSport) => schoolSport.sport_id,
    ),
    hidden: (values) => ["head_of_sports", "brand_ambassador"].includes(values.role),
    validate: (value, values) =>
      ["head_of_sport", "coach"].includes(String(values.role)) && !String(value || "").trim()
        ? "Select a sport for this role."
        : undefined,
  },
  {
    name: "active",
    label: "Active assignment",
    kind: "checkbox",
  },
];

export const buildSchoolRoleCreateValues = () => ({
  user_id: "",
  role: "head_of_sports",
  sport_id: "",
  active: true,
});

export const buildSchoolRoleUpdateValues = (row: any) => ({
  user_id: row.user_id || row.user?.id || "",
  role: row.role || "",
  sport_id: row.sport_id || "",
  active: Boolean(row.active),
});

export const buildSchoolRoleStats = (assignments: any[] = []) => {
  const activeAssignments = assignments.filter((assignment: any) => assignment.active !== false);

  return [
    {
      helper: "Assignments currently reflected in the live reporting chart.",
      label: "Active Roles",
      value: activeAssignments.length,
    },
    {
      helper: "Head of Sports and Head of Sport leadership roles.",
      label: "Leadership",
      value: activeAssignments.filter((assignment: any) =>
        ["head_of_sports", "head_of_sport"].includes(String(assignment.role)),
      ).length,
    },
    {
      helper: "Sport branches with at least one active role mapped.",
      label: "Sport Branches",
      value: new Set(
        activeAssignments
          .filter((assignment: any) => assignment.sport_id || assignment.sport_name)
          .map((assignment: any) => String(assignment.sport_id || assignment.sport_name)),
      ).size,
    },
    {
      helper: "Brand ambassadors and coach assignments in the current school.",
      label: "Support Roles",
      value: activeAssignments.filter((assignment: any) =>
        ["brand_ambassador", "coach"].includes(String(assignment.role)),
      ).length,
    },
  ];
};

export const buildSchoolSportFields = (sports: any[]): CrudFieldConfig[] => [
  {
    name: "sport_id",
    label: "Sport",
    kind: "select",
    options: toSelectOptions(sports, (sport) => sport.name || "Unknown"),
    required: true,
  },
  {
    name: "status",
    label: "Status",
    kind: "select",
    options: [
      { label: "Active", value: "active" },
      { label: "Inactive", value: "inactive" },
    ],
    required: true,
  },
  {
    name: "notes",
    label: "Notes",
    kind: "textarea",
    placeholder: "Programme notes, squads covered, or seasonal context",
  },
];

export const buildSchoolSportCreateValues = (sports: any[]) => ({
  sport_id: sports?.[0]?.id || "",
  status: "active",
  notes: "",
});

export const buildSchoolSportUpdateValues = (row: any) => ({
  sport_id: row.sport_id || "",
  status: row.status || "active",
  notes: row.notes || "",
});

export const getSchoolDocumentPermissions = (portalAccess: any) => ({
  canManageSchoolDocuments: hasAnyPortalAction(portalAccess, schoolDocumentManageActions),
  canManageSchoolGallery: hasAnyPortalAction(portalAccess, schoolGalleryManageActions),
  canVerifySchoolDocuments: hasAnyPortalAction(portalAccess, schoolDocumentVerifyActions),
});
